import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribe, settleConfirm } from './confirm';
import './confirm.css';

/**
 * The one place a confirmation is drawn. Mounted once, in App.js, in
 * the signed-in tree beside ToastHost. Portalled to <body> for the same
 * reason the export dialog is: position:fixed is measured from the
 * nearest transformed ancestor, and these open from deep inside pages
 * and from inside other popups.
 *
 * The frame is the house popup's (see confirm.css). Cancel is on the
 * left of the pair and the answer on the right; when the answer is
 * destructive, focus starts on Cancel, so Enter never deletes anything
 * by default.
 */
const ConfirmHost = () => {
  const [item, setItem] = useState(null);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => subscribe(setItem), []);

  // A fresh dialog starts clean and puts focus where it belongs.
  useEffect(() => {
    if (!item) return undefined;
    setValue(item.input?.value || '');
    setTouched(false);
    const t = setTimeout(() => {
      if (item.input) inputRef.current?.focus();
      else if (item.tone === 'danger') cancelRef.current?.focus();
      else confirmRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [item]);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        settleConfirm('dismiss');
      }
    };
    // Capture, so an Escape meant for this dialog does not also close
    // the view popup underneath it.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [item]);

  if (!item || typeof document === 'undefined') return null;

  const missing = Boolean(item.input?.required) && !value.trim();

  const answer = () => {
    if (missing) {
      setTouched(true);
      inputRef.current?.focus();
      return;
    }
    settleConfirm('confirm', item.input ? value : undefined);
  };

  const InputTag = item.input?.type === 'textarea' ? 'textarea' : 'input';

  return createPortal(
    <div
      className="cnf"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) settleConfirm('dismiss');
      }}
    >
      <div
        className={`cnf-panel cnf-panel--${item.tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`cnf-title-${item.id}`}
        aria-describedby={item.text ? `cnf-text-${item.id}` : undefined}
      >
        <header className="cnf-head">
          <h2 className="cnf-title" id={`cnf-title-${item.id}`}>
            {item.title}
          </h2>
          <button
            type="button"
            className="cnf-close"
            onClick={() => settleConfirm('dismiss')}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {(item.text || item.input) && (
          <div className="cnf-body">
            {item.text && (
              <p className="cnf-text" id={`cnf-text-${item.id}`}>
                {item.text}
              </p>
            )}

            {item.input && (
              <label className="cnf-field">
                {item.input.label && (
                  <span className="cnf-field-label">{item.input.label}</span>
                )}
                <InputTag
                  ref={inputRef}
                  className={`cnf-input${touched && missing ? ' is-bad' : ''}`}
                  type={InputTag === 'input' ? 'text' : undefined}
                  rows={InputTag === 'textarea' ? 3 : undefined}
                  value={value}
                  placeholder={item.input.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && InputTag === 'input') {
                      e.preventDefault();
                      answer();
                    }
                  }}
                />
                {touched && missing && (
                  <span className="cnf-field-error">This is required.</span>
                )}
              </label>
            )}
          </div>
        )}

        <footer className="cnf-foot">
          <button
            ref={cancelRef}
            type="button"
            className="cnf-btn"
            onClick={() => settleConfirm('dismiss')}
          >
            {item.cancelText}
          </button>
          {item.denyText && (
            <button
              type="button"
              className="cnf-btn"
              onClick={() => settleConfirm('deny')}
            >
              {item.denyText}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            className={`cnf-btn ${item.tone === 'danger' ? 'cnf-btn--danger' : 'cnf-btn--main'}`}
            onClick={answer}
          >
            {item.confirmText}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmHost;
